<template>
  <div class="mt-2">
    <LiquidatedDamagesAlert
      :contract="contract"
      :liquidatedDamages="liquidatedDamages"
      :capFromAdjustedContractPrice="capFromAdjustedContractPrice"
    />
    <v-data-table
      :headers="headers"
      :items="liquidatedDamages"
      :single-expand="true"
      show-expand
    >
    <template v-slot:item.milestoneName="{ item }">
      <v-chip
        small
        :color="chipColor(item)"
      >
        {{item.milestoneName}}
      </v-chip>
    </template>
      <template v-slot:item.name="{ item }">
      <span>{{ item.name }}</span>
      </template>
      <template v-slot:item.originalDate="{ item }">
        <span class="nowrap">{{ item.originalDate | formatDate }}</span>
      </template>
      <template v-slot:item.eot="{ item }">
        <strong>{{ item.eot }}</strong>
      </template>
      <template v-slot:item.amendedCompletionDate="{ item }">
        <span class="nowrap">{{ item.amendedCompletionDate | formatDate }}</span>
      </template>
      <template v-slot:item.actualCompletionDate="{ item }">
        <span class="nowrap">{{ item.actualCompletionDate | formatDate }}</span>
      </template>
      <template v-slot:item.delay="{ item }">
        <strong>{{ item.delay }}</strong>
      </template>
      <template v-slot:item.totalValueCents="{ item }">
        <PricesDisplay
          :prices="item.totalValueCents"
          :contractCurrencies="contract.currencies"
          :chosenCurrency="contract.chosenCurrency"
          :itemCurrency="getCurrencyForItem(item)"
        />
      </template>
      <template v-slot:item.status="{ item }">
          <v-chip
            v-if="item.status"
            color="primary"
            small
          >
          {{ item.status }}
          </v-chip>
      </template>
      <template v-slot:item.documentUrl="{ item }">
        <v-row justify="center">
          <v-btn
            v-if="item.documentUrl"
            icon
            small
            :href="item.documentUrl"
            target="_blank"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </v-row>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          @click="handleEditLiquidatedDamageModal(item)"
          :disabled="!canUserEdit"
        >
          <v-icon>mdi-pencil </v-icon>
        </v-btn>
        <SubmitButton
          icon="mdi-delete"
          small
          :disabled="!canUserEdit"
          @click="$emit('DELETE_LD', item)"
        />
      </template>
      <template v-slot:expanded-item="{ item }">
        <td
          class="white py-8"
          :colspan="headers.length"
        >
          <v-row>
            <v-spacer></v-spacer>
            <v-btn
              rounded
              dense
              small
              color="primary"
              @click="handleAddOrUpdateLiquidatedDamageDate(item)"
              :disabled="!canUserEdit"
            >
              <v-icon> mdi-plus </v-icon>
              {{ $t('lds.addLiquidatedDamageDate') }}
            </v-btn>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card>
                <LiquidatedDamagesDateTable
                  :dates="item.liquidatedDamageDates"
                  :contract="contract"
                  :canUserEdit="canUserEdit"
                  :currency="getCurrencyForItem(item)"
                  @DELETE_LD_DATE="i => $emit('DELETE_LD_DATE', i)"
                  @EDIT_LD_DATE="date => handleAddOrUpdateLiquidatedDamageDate(item, date)"
                />
              </v-card>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
    <LiquidatedDamagesCap
      :canUserEdit="canUserEdit"
      :contract="contract"
      :capFromAdjustedContractPrice="capFromAdjustedContractPrice"
    />
  </div>
</template>

<script>
import ContractInfoMixin from '../../mixins/ContractInfoMixin'
import { mapState } from 'vuex'

export default {
  name: 'LiquidatedDamagesTable',
  props: {
    contract: Object,
    liquidatedDamages: Array,
    isMilestoneNameDuplicate: Function
  },
  mixins: [ContractInfoMixin],
  data () {
    return {
      headers: this.getHeaders()
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    capPercent () {
      return this.contract.liquidatedDamagesCap.valuePercent
    },
    capFromAdjustedContractPrice () {
      return this.capPercent ? this.calculateCapFromAdjustedContractPrice(this.capPercent) : {}
    }
  },
  methods: {
    getCurrencyForItem (item) {
      const part = this.contract.parts.find(part => part.id === item.partId)
      return part ? this.contract.currencies.find(currency => currency.key === part.currency) : this.contract.currencies[0]
    },
    chipColor (item) {
      return item.finalized ? 'yellow' : ''
    },
    getHeaders () {
      return [
        { text: this.$t('lds.milestoneName'), value: 'milestoneName' },
        { text: this.$t('lds.name'), value: 'name' },
        { text: this.$t('lds.originalDate'), value: 'originalDate' },
        { text: this.$t('lds.eot'), value: 'eot' },
        { text: this.$t('lds.amendedDate'), value: 'amendedCompletionDate' },
        { text: this.$t('lds.actualDate'), value: 'actualCompletionDate' },
        { text: this.$t('lds.delay'), value: 'delay' },
        { text: this.$t('lds.totalValue'), value: 'totalValueCents' },
        { text: this.$t('common.status'), value: 'status' },
        { text: this.$t('lds.document'), value: 'documentUrl' },
        { text: '', value: 'actions', sortable: false, width: 120 },
        { text: '', value: 'data-table-expand' }
      ]
    },
    handleEditLiquidatedDamageModal (item) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'LiquidatedDamagesForm',
        options: { maxWidth: 850 },
        scope: {
          liquidatedDamage: item,
          contractId: this.contract.id,
          isMultiCurrency: this.contract.isMultiCurrency,
          currencies: this.contract.currencies,
          parts: this.contract.parts,
          isMilestoneNameDuplicate: this.isMilestoneNameDuplicate
        },
        handlers: {
          SAVE_LD: (data) => this.$emit('SAVE_LD', data),
          FINALIZE_LD: () => this.$emit('FINALIZE_LD'),
          UNLOCK_LD: () => this.$emit('UNLOCK_LD')
        }
      })
    },
    handleAddOrUpdateLiquidatedDamageDate (item, date) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'LiquidatedDamagesDateForm',
        options: { maxWidth: 850 },
        scope: {
          liquidatedDamage: item,
          part: item.partId ? this.contract.parts.find(p => p.id === item.partId) : null,
          currencies: this.contract.currencies,
          date
        },
        handlers: {
          SAVE_LD_DATE: (data) => this.$emit('SAVE_LD_DATE', data)
        }
      })
    },
    calculateCapFromAdjustedContractPrice (percent) {
      const array = Object.entries(this.adjustedPriceCentsPerCurrency)
      const calculatedCap = array.map(([key, value]) => [key, Number(this.$options.filters.toFixed(value * percent))])
      return Object.fromEntries(calculatedCap)
    }
  }
}
</script>
